import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { compressImageIfNeeded } from "@/lib/imageCompress";
import useVideoCompressor from "@/hooks/useVideoCompressor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Videos above this size get compressed with ffmpeg.wasm client-side to avoid
// ingress 502 / gateway timeouts on long uploads.
const VIDEO_COMPRESS_THRESHOLD = 10 * 1024 * 1024; // 10 MB

/**
 * Reusable image uploader.
 * onUploaded(url) - called with the absolute image URL after upload.
 * If `multiple` is true, files upload sequentially and onUploaded is called once
 * per file (kind="photo"). Aggregated progress is shown in the button.
 */
export default function ImageUploader({ onUploaded, label = "Enviar foto", testid = "upload-btn", variant = "outline", size = "sm", className = "", accept = "image/*", multiple = false, maxFiles = 10 }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [batchIdx, setBatchIdx] = useState(0);
  const [batchTotal, setBatchTotal] = useState(0);
  const { compress: compressVideo, progress: videoProgress } = useVideoCompressor();

  const uploadOne = async (file, onProgress) => {
    const isVideo = file.type.startsWith("video/");
    if (isVideo) {
      if (file.size > VIDEO_COMPRESS_THRESHOLD) {
        setCompressing(true);
        const originalSizeMB = (file.size / 1024 / 1024).toFixed(1);
        toast.info(`Comprimindo vídeo de ${originalSizeMB}MB…`);
        try {
          const compressed = await compressVideo(file);
          if (compressed) {
            const newSizeMB = (compressed.size / 1024 / 1024).toFixed(1);
            toast.success(`Vídeo reduzido: ${originalSizeMB}MB → ${newSizeMB}MB`);
            file = compressed;
          }
        } catch (cErr) {
          console.warn("video compression failed, uploading original", cErr);
          toast.warning("Não foi possível comprimir — enviando original");
        } finally {
          setCompressing(false);
        }
      }
    } else {
      file = await compressImageIfNeeded(file);
    }
    const fd = new FormData();
    fd.append("file", file);
    const res = await axios.post(`${API}/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
      onUploadProgress: (evt) => {
        if (evt.total && onProgress) onProgress(evt.loaded / evt.total);
      },
    });
    return { url: res.data.url, isVideo };
  };

  const handleFile = async (e) => {
    const filesRaw = Array.from(e.target.files || []);
    if (filesRaw.length === 0) return;
    const files = multiple ? filesRaw.slice(0, maxFiles) : [filesRaw[0]];
    if (multiple && filesRaw.length > maxFiles) {
      toast.warning(`Máximo de ${maxFiles} arquivos — apenas os primeiros foram usados`);
    }
    setUploading(true);
    setBatchTotal(files.length);
    setBatchIdx(0);
    setUploadPct(0);
    try {
      for (let i = 0; i < files.length; i++) {
        setBatchIdx(i + 1);
        setUploadPct(0);
        const { url, isVideo } = await uploadOne(files[i], (p) => setUploadPct(Math.min(99, Math.round(p * 100))));
        setUploadPct(100);
        onUploaded?.(url, isVideo ? "video" : "photo");
      }
      toast.success(files.length > 1 ? `${files.length} fotos enviadas!` : "Enviado!");
    } catch (err) {
      const msg = err.response?.data?.detail
        || (err.code === "ECONNABORTED" ? "Tempo esgotado — tente uma foto menor ou verifique sua conexão" : null)
        || "Erro no upload";
      toast.error(msg);
    } finally {
      setUploading(false);
      setCompressing(false);
      setUploadPct(0);
      setBatchIdx(0);
      setBatchTotal(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const busyLabel = compressing
    ? `Comprimindo ${Math.round(videoProgress * 100)}%…`
    : batchTotal > 1
      ? `Foto ${batchIdx}/${batchTotal} — ${uploadPct}%`
      : `Enviando ${uploadPct}%…`;

  return (
    <>
      <input
        ref={inputRef}
        data-testid={`${testid}-input`}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleFile}
        className="hidden"
      />
      <Button
        data-testid={testid}
        type="button"
        variant={variant}
        size={size}
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className={`relative overflow-hidden ${className}`}
      >
        {/* progress fill (animated) */}
        {uploading && (
          <span
            data-testid={`${testid}-progress-bar`}
            aria-hidden="true"
            className="absolute inset-y-0 left-0 bg-primary/20 transition-[width] duration-200 pointer-events-none"
            style={{ width: `${compressing ? Math.round(videoProgress * 100) : uploadPct}%` }}
          />
        )}
        <span className="relative flex items-center">
          {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-2" />}
          <span data-testid={`${testid}-label`}>{uploading ? busyLabel : label}</span>
        </span>
      </Button>
    </>
  );
}
